import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, History, FileText, Search, ChevronLeft, ChevronRight, Check, Clock } from "lucide-react";
import { createBulkStyles, BULK_CSS } from "./BulkActions/bulkActions.styles";
import { Btn } from "./BulkActions/BulkSteps";
import ReusableDropdown from "../../../components/ReusableDropdown";
import Pagination from "../../../components/Pagination";

const PAGE_SIZE = 12;

const STATUS_COLORS = {
  approved: "#10b981",
  completed: "#10b981",
  pending: "#f59e0b",
  scheduled: "#3b82f6",
  rejected: "#ef4444",
  cancelled: "#94a3b8",
};

// Keys rendered elsewhere in the details panel (or not user-facing at all).
const ALREADY_SHOWN = new Set([
  "id", "actionType", "employeeId", "infinityCode", "employeeName", "employeeArabicName",
  "status", "reason", "effectiveDate", "createdAt", "requestedDate", "approvedBy",
  "rejectionReason", "updatedAt", "__typename", "employee", "clientCode", "approverNotes",
  "oldPosition", "newPosition", "oldDepartment", "newDepartment", "oldSalary", "newSalary",
  "oldGrossSalary", "newGrossSalary", "oldInsuranceSalary", "newInsuranceSalary",
  "oldReportsTo", "newReportsTo", "salaryChange", "changePercent", "oldData", "newData",
  "workflowProgress", "workflowInstanceId",
]);

/**
 * Action history — same shell as the Employee Action / Pending Approvals
 * modals: toolbar (search, status, action type), a paged table, and a details
 * panel that opens beside the table when a row is clicked.
 */
const HistoryModal = ({
  t,
  theme,
  isRTL,
  language,
  actionHistory,
  ACTION_TYPES,
  hasFeatureAccess,
  onClose,
  // Manager view: no salary numbers (the server doesn't send them either).
  hideSalary = false,
}) => {
  const s = useMemo(() => createBulkStyles(theme, isRTL), [theme, isRTL]);
  const k = s.k;
  const lang = language === "ar" ? "ar" : "en";
  const shellRef = useRef(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  // Admin-level feature gating (registry "changesPage.viewactiondetails") —
  // a promoted user without this feature can still see the history list but
  // can't open a row's full details.
  const canViewDetails = !hasFeatureAccess || hasFeatureAccess("changesPage", "viewactiondetails");

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (selected) setSelected(null);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, onClose]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, typeFilter]);

  const locale = lang === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const fmtDate = (v) => {
    if (!v) return t.notAvailable;
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? String(v)
      : new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(d);
  };
  const fmtNum = (v) => Number(v).toLocaleString(locale, { maximumFractionDigits: 2 });
  const arrow = isRTL ? "←" : "→";

  const nameOf = (a) =>
    (lang === "ar" ? a.employeeArabicName || a.employeeName : a.employeeName || a.employeeArabicName) || t.notAvailable;
  const codeOf = (a) => a.clientCode || a.employee?.clientCode || a.infinityCode || a.employeeId || "";
  const dateOf = (a) => a.effectiveDate || a.createdAt || a.requestedDate;
  const configOf = (a) => ACTION_TYPES.find((x) => x.id === a.actionType);
  const statusKey = (a) => String(a.status || "").toLowerCase();

  const statusPill = (a) => {
    const key = statusKey(a);
    const color = STATUS_COLORS[key] || "#94a3b8";
    const Icon = key === "approved" || key === "completed" ? Check : key === "pending" ? Clock : key ? X : null;
    return (
      <span style={s.pill(color)}>
        {Icon && <Icon size={12} strokeWidth={3} />}
        {t[key] || a.status || t.unknown}
      </span>
    );
  };

  const typeBadge = (a) => {
    const cfg = configOf(a);
    const Icon = cfg?.icon || FileText;
    return (
      <span style={s.actionBadge(cfg?.bgColor || "rgba(100,116,139,0.15)", cfg?.borderColor || "rgba(100,116,139,0.4)")}>
        <Icon size={13} />
        {cfg ? t[cfg.labelKey] : a.actionType}
      </span>
    );
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const list = useMemo(() => (Array.isArray(actionHistory) ? actionHistory : []), [actionHistory]);
  const statusCounts = useMemo(() => {
    const c = {};
    list.forEach((a) => {
      const key = statusKey(a);
      if (key) c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [list]);
  const statusTabs = ["all", ...["approved", "completed", "pending", "scheduled", "rejected", "cancelled"].filter((st) => statusCounts[st])];
  const typesPresent = useMemo(
    () => ACTION_TYPES.filter((cfg) => list.some((a) => a.actionType === cfg.id)),
    [ACTION_TYPES, list]
  );

  const term = search.trim().toLowerCase();
  const filtered = list.filter((a) => {
    if (statusFilter !== "all" && statusKey(a) !== statusFilter) return false;
    if (typeFilter && a.actionType !== typeFilter) return false;
    if (!term) return true;
    return [a.employeeName, a.employeeArabicName, codeOf(a), a.reason]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Details panel ─────────────────────────────────────────────────────────
  const renderDetails = (a) => {
    const cfg = configOf(a);
    const Icon = cfg?.icon || FileText;
    const changes = [
      [t.position, a.oldPosition ?? a.oldData?.position, a.newPosition ?? a.newData?.position],
      [t.department, a.oldDepartment ?? a.oldData?.department, a.newDepartment ?? a.newData?.department],
      [t.directManager, a.oldReportsTo ?? a.oldData?.reportsTo, a.newReportsTo ?? a.newData?.reportsTo],
      ...(!hideSalary
        ? [
            [lang === "ar" ? "صافي الراتب" : "Net salary", a.oldSalary ?? a.oldData?.salary, a.newSalary ?? a.newData?.salary, true],
            [lang === "ar" ? "الراتب الإجمالي" : "Gross salary", a.oldGrossSalary, a.newGrossSalary, true],
            [lang === "ar" ? "الأجر التأميني" : "Insurance salary", a.oldInsuranceSalary, a.newInsuranceSalary, true],
          ]
        : []),
    ].filter(([, from, to]) => to !== null && to !== undefined && to !== "" && String(to) !== String(from ?? ""));

    const humanize = (key) => key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
    const formatValue = (value) => {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "boolean") return value ? t.yes : t.no;
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return fmtDate(value);
      if (typeof value === "object") return null;
      return String(value);
    };
    // Each action type stores different extra fields — show whatever exists.
    const extra = Object.entries(a)
      .filter(([key]) => !ALREADY_SHOWN.has(key))
      .filter(([key]) => !hideSalary || !/salary|changePercent|percentChange/i.test(key))
      .map(([key, value]) => [key, formatValue(value)])
      .filter(([, value]) => value !== null);

    const label = { fontSize: 12.5, fontWeight: 600, color: k.muted };
    const block = { padding: "12px 14px", borderRadius: 12, border: `1px solid ${k.border}`, background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff" };
    const section = { fontSize: 12.5, fontWeight: 800, color: k.muted, textTransform: "uppercase", letterSpacing: "0.05em", margin: "18px 0 8px" };
    const Back = isRTL ? ChevronRight : ChevronLeft;

    return (
      <aside className="nx-hm-aside">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14 }}>
          <button
            type="button"
            className="nx-hm-back"
            onClick={() => setSelected(null)}
            style={{ border: "none", background: "none", color: k.accent, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit" }}
          >
            <Back size={15} /> {t.back}
          </button>
          <button type="button" className="nxb-btn" style={{ ...s.iconButton, width: 30, height: 30, borderRadius: 8 }} aria-label={t.close} onClick={() => setSelected(null)}>
            <X size={15} />
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ ...s.actionIcon(cfg?.color || "#64748b"), width: 44, height: 44, borderRadius: 12, marginBottom: 0, flexShrink: 0 }}>
            <Icon size={21} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{cfg ? t[cfg.labelKey] : a.actionType}</div>
            <div style={{ marginTop: 4 }}>{statusPill(a)}</div>
          </div>
        </div>

        <div style={{ ...block, marginTop: 16, display: "grid", gap: 8 }}>
          {[
            [t.employee, nameOf(a)],
            [t.code, codeOf(a)],
            [t.effectiveDate, fmtDate(dateOf(a))],
            a.createdAt && [t.requestedDate, fmtDate(a.createdAt)],
            a.approvedBy && [t.approvedBy, a.approvedBy],
          ]
            .filter(Boolean)
            .map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
                <span style={{ color: k.muted }}>{l}</span>
                <bdi style={{ fontWeight: 700, textAlign: "end" }}>{v || t.notAvailable}</bdi>
              </div>
            ))}
        </div>

        {changes.length > 0 && (
          <>
            <div style={section}>{t.changesTitle}</div>
            <div style={{ display: "grid", gap: 8 }}>
              {changes.map(([l, from, to, money]) => {
                const up = money && from != null ? Number(to) >= Number(from) : undefined;
                return (
                  <div key={l} style={block}>
                    <div style={label}>{l}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, marginTop: 3 }}>
                      <bdi style={{ color: k.muted, fontWeight: 500 }}>{from != null && from !== "" ? (money ? fmtNum(from) : from) : "—"}</bdi>
                      {` ${arrow} `}
                      <bdi style={{ color: up === undefined ? k.text : up ? "#10b981" : "#ef4444" }}>
                        {money ? `${fmtNum(to)} ${t.egp}` : to}
                      </bdi>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {(a.reason || a.approverNotes || a.rejectionReason) && (
          <>
            <div style={section}>{t.reasonAndNotes}</div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                [t.reasonForAction || t.reason, a.reason],
                [t.approverNotes, a.approverNotes],
                [t.rejectionReason, a.rejectionReason],
              ]
                .filter(([, v]) => v)
                .map(([l, v]) => (
                  <div key={l} style={{ ...block, ...(l === t.rejectionReason ? { borderColor: "rgba(239,68,68,0.35)" } : null) }}>
                    <div style={label}>{l}</div>
                    <bdi style={{ display: "block", fontSize: 14, lineHeight: 1.55, marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{v}</bdi>
                  </div>
                ))}
            </div>
          </>
        )}

        {extra.length > 0 && (
          <>
            <div style={section}>{t.otherDetails}</div>
            <div style={{ ...block, display: "grid", gap: 8 }}>
              {extra.map(([key, value]) => (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13.5 }}>
                  <span style={{ color: k.muted }}>{t[key] || humanize(key)}</span>
                  <bdi style={{ fontWeight: 600, textAlign: "end", wordBreak: "break-word" }}>{value}</bdi>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>
    );
  };

  const th = { ...s.th, background: k.dark ? "#1e293b" : "#f8fafc", padding: "12px 14px" };
  const td = { ...s.td, padding: "12px 14px", verticalAlign: "middle" };
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="nxb-overlay" style={{ ...s.overlay, zIndex: 1000 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <style>{BULK_CSS}</style>
      <style>{`
        .nx-hm-body { flex: 1; min-height: 0; display: flex; position: relative; }
        .nx-hm-main { flex: 1; min-width: 0; overflow-y: auto; padding: 16px 28px; }
        .nx-hm-aside {
          width: 400px; flex-shrink: 0; box-sizing: border-box; overflow-y: auto; padding: 18px 20px;
          background: ${k.subtle}; border-inline-start: 1px solid ${k.border};
          animation: nxbFade .2s ease;
        }
        .nx-hm-row { cursor: pointer; }
        .nx-hm-row:hover td { background: ${k.dark ? "rgba(46,124,186,0.10)" : "rgba(46,124,186,0.05)"}; }
        .nx-hm-row[aria-selected="true"] td { background: ${k.dark ? "rgba(46,124,186,0.18)" : "rgba(46,124,186,0.09)"}; }
        .nx-hm-search:focus { border-color: #2e7cba !important; }
        @media (max-width: 1000px) {
          .nx-hm-aside { position: absolute; inset: 0; width: auto; z-index: 5; border: none; }
        }
      `}</style>
      <div
        ref={shellRef}
        className="nxb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-hm-title"
        tabIndex={-1}
        style={{
          ...s.shell,
          width: list.length ? "min(1320px, 100%)" : "min(560px, 100%)",
          height: list.length ? "min(820px, 95vh)" : "auto",
          position: "relative",
          outline: "none",
        }}
      >
        <div className="nxb-header" style={{ ...s.header, padding: "12px 28px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            <div style={{ ...s.headerIcon, width: 46, height: 46, borderRadius: 13 }}><History size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="nx-hm-title" style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: "-0.01em" }}>{t.actionHistory}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 14, color: k.muted }}>
                {list.length} {t.actions}
              </p>
            </div>
          </div>
          <button type="button" className="nxb-btn" style={s.iconButton} aria-label={t.close} title={t.close} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {list.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 28px", borderBottom: `1px solid ${k.border}`, background: k.subtle }}>
            <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 380 }}>
              <Search size={16} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", insetInlineStart: 12, color: k.muted }} />
              <input className="nx-hm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.historySearch} style={s.search} />
            </div>
            <div style={{ ...s.segmented, flexWrap: "wrap" }} role="radiogroup" aria-label={t.status}>
              {statusTabs.map((st) => (
                <button
                  key={st}
                  type="button"
                  role="radio"
                  aria-checked={statusFilter === st}
                  className="nxb-seg"
                  style={{ ...s.segment(statusFilter === st), padding: "7px 12px", fontSize: 13.5 }}
                  onClick={() => setStatusFilter(st)}
                >
                  {st !== "all" && <span style={s.dot(STATUS_COLORS[st])} />}
                  {st === "all" ? t.historyAll : t[st] || st}
                  <span style={{ fontSize: 12, color: k.muted, fontWeight: 700 }}>({st === "all" ? list.length : statusCounts[st]})</span>
                </button>
              ))}
            </div>
            {typesPresent.length > 1 && (
              <div style={{ width: 230, position: "relative" }}>
                <ReusableDropdown
                  items={[{ id: "", name: t.allActionTypes }, ...typesPresent.map((cfg) => ({ id: cfg.id, name: t[cfg.labelKey] }))]}
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(v || "")}
                  onClear={() => setTypeFilter("")}
                  getItemKey={(it) => it.id}
                  getItemLabel={(it) => it.name}
                  placeholder={t.allActionTypes}
                  theme={theme}
                  language={language}
                  showAllOnArrowClick={true}
                />
              </div>
            )}
          </div>
        )}

        <div className="nx-hm-body" data-open={selected && canViewDetails ? "true" : "false"}>
          <div className="nx-hm-main">
            {list.length === 0 ? (
              <div style={{ textAlign: "center", padding: "28px 12px" }}>
                <div style={s.dropIcon}><History size={28} /></div>
                <h3 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 800 }}>{t.noActionHistory}</h3>
                <p style={{ margin: 0, fontSize: 14, color: k.muted }}>{t.noActionsRecorded}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: k.muted, fontSize: 14 }}>{t.noEmployeesMatch}</div>
            ) : (
              <>
                <div style={{ border: `1px solid ${k.border}`, borderRadius: 14, overflow: "hidden" }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={th}>{t.date}</th>
                        <th style={th}>{t.employee}</th>
                        <th style={th}>{t.actionType}</th>
                        <th style={th}>{t.status}</th>
                        {canViewDetails && <th style={{ ...th, width: 32 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a) => {
                        const isSel = selected?.id === a.id;
                        return (
                          <tr
                            key={a.id}
                            className={canViewDetails ? "nx-hm-row" : undefined}
                            aria-selected={isSel}
                            onClick={canViewDetails ? () => setSelected(a) : undefined}
                            title={canViewDetails ? (isRTL ? "اضغط لعرض التفاصيل" : "Click to see details") : undefined}
                          >
                            <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{fmtDate(dateOf(a))}</td>
                            <td style={td}>
                              <div style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{nameOf(a)}</div>
                              {codeOf(a) && <div style={{ fontSize: 12.5, color: k.muted, marginTop: 2 }}>{codeOf(a)}</div>}
                            </td>
                            <td style={td}>{typeBadge(a)}</td>
                            <td style={td}>{statusPill(a)}</td>
                            {canViewDetails && (
                              <td style={{ ...td, color: k.muted, padding: "12px 10px" }}>
                                <Chevron size={16} />
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} theme={theme} isRTL={isRTL} />
                )}
              </>
            )}
          </div>
          {selected && canViewDetails && renderDetails(selected)}
        </div>

        <div className="nxb-footer" style={{ ...s.footer, padding: "12px 28px" }}>
          <span style={{ fontSize: 13.5, color: k.muted }}>
            {filtered.length !== list.length && (
              <>
                {t.showing} <strong style={{ color: k.text }}>{filtered.length}</strong> {t.of} {list.length}
              </>
            )}
          </span>
          <Btn s={s} variant="secondary" onClick={onClose}>{t.close}</Btn>
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
